import { Injectable, OnDestroy } from '@angular/core';

import { OfferSource } from '@cds';
import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import { CdsPushService } from '@frontend/sports/content-distribution/feature';
import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { Store } from '@ngrx/store';
import { flatten } from 'lodash-es';
import { FreshDataType } from 'packages/sports/web/app/src/data-refresh/fresh-data-provider.service';
import { FreshDataService } from 'packages/sports/web/app/src/data-refresh/fresh-data.service';
import { Subscription, filter } from 'rxjs';

import { BetBuilderPickId, GroupPickId, PickId, V1PickId, V2OptionMarketPickId, V2ParticipantPickId } from '../../../core/picks/pick-id';
import { CdsSubscription, CdsSubscriptionFactory } from '../../../integration/services/cds-subscription-factory';
import { IPickSubscriptionPayload } from '../pick-subscription-actions';
import { SubscriptionCallBacksPool } from './subscription-call-backs.pool';

interface IPickSubscription {
    unsubscribe: () => void;
    cdsSubscriptions: CdsSubscription[];
    fixtureId: string;
    subscriptionContext: string[];
}

/**
 * Betslip subscription service manager.
 */
@Injectable()
export default class BetslipPickSubscriptionService implements OnDestroy {
    private subscriptionStore: Map<string, { pickId: PickId; subscriptionInfo: IPickSubscription }> = new Map<
        string,
        { pickId: PickId; subscriptionInfo: IPickSubscription }
    >();

    protected subscriptionsCallbacksPool: SubscriptionCallBacksPool = new SubscriptionCallBacksPool(this.store);
    private disconnectedSub: Subscription;

    constructor(
        private cdsPush: CdsPushService,
        private freshData: FreshDataService,
        private connection: ConnectionConfig,
        protected store: Store,
        protected subscriptionFactory: CdsSubscriptionFactory,
    ) {
        this.disconnectedSub = this.freshData.reloadNeeded
            .pipe(
                filter((type) => type === FreshDataType.DataAndPush),
                filterSportsEmitLast(),
            )
            .subscribe(() => {
                this.onPushReconnected();
            });
    }

    subscribeToPicks(picks: IPickSubscriptionPayload[]): Promise<void[]> {
        if (!picks.length) {
            return Promise.resolve([]);
        }

        return Promise.all(picks.map((p) => this.subscribeToPick(p)));
    }

    unsubscribeFromPicks(picks: PickId[]): void {
        for (const p of picks) {
            this.unsubscribeFromPick(p);
        }
    }

    unsubscribeFromAll(): void {
        for (const subscription of this.subscriptionStore.values()) {
            subscription.subscriptionInfo.unsubscribe();
        }

        this.cleanStores();
    }

    ngOnDestroy(): void {
        this.unsubscribeFromAll();
        this.disconnectedSub.unsubscribe();
    }

    private subscribeToPush(payload: IPickSubscriptionPayload, subscriptions: CdsSubscription[]): void {
        const { pickId, context } = payload;
        const pickIdStr = pickId.toString();
        // Get all current subscriptions
        const currentPickSubs = flatten(Array.from(this.subscriptionStore.values()).map(({ subscriptionInfo }) => subscriptionInfo.cdsSubscriptions));
        for (const sub of subscriptions) {
            const existingSubscription = currentPickSubs.find((_) => _.key === sub.key);
            if (!existingSubscription) {
                // If not currently there is a pick with same subscription key subscribe.
                this.cdsPush.subscribe(sub);
            }
        }
        const pickSubscription: IPickSubscription = {
            unsubscribe: () => {
                const pickSub = this.subscriptionStore.get(pickIdStr);
                if (!pickSub) {
                    return;
                }
                // Get subscriptions of the other picks.
                const otherPickSubscriptions = Array.from(this.subscriptionStore.entries())
                    .filter(([subPickId, _]) => subPickId.toString() !== pickId.toString())
                    .map(([_, sub]) => sub.subscriptionInfo.cdsSubscriptions);
                for (const s of pickSub.subscriptionInfo.cdsSubscriptions) {
                    if (!otherPickSubscriptions.some((ps) => ps.some((_) => _.key === s.key))) {
                        // if no other pick has the same subscription, unsubscribe from it.
                        this.cdsPush.unsubscribe(s);
                    }
                }
                this.subscriptionStore.delete(pickIdStr);
            },
            cdsSubscriptions: subscriptions,
            fixtureId: pickId.fixtureId,
            subscriptionContext: context,
        };
        this.subscriptionStore.set(pickIdStr, { pickId, subscriptionInfo: pickSubscription });
    }

    private subscribeToPick(payload: IPickSubscriptionPayload): void {
        const { pickId, context, fromHybridFixture } = payload;
        if (this.subscriptionStore.has(pickId.toString()) || context.length === 0) {
            return;
        }

        const subscriptions: CdsSubscription[] = [];
        const lang = this.connection.culture;

        if (pickId instanceof V1PickId && !fromHybridFixture) {
            const gameSubscription = this.subscriptionFactory.createGameSubscription(context, pickId.fixtureId, pickId.marketId.toString(), lang);
            gameSubscription.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.V1, gameSubscription.key);
            subscriptions.push(gameSubscription);
        }

        if (pickId instanceof V1PickId && fromHybridFixture) {
            const optionMarketSubscription = this.subscriptionFactory.createOptionMarketSubscription(
                context,
                pickId.fixtureId,
                pickId.marketId.toString(),
                lang,
            );
            optionMarketSubscription.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.V1, optionMarketSubscription.key);
            subscriptions.push(optionMarketSubscription);
        }

        if (pickId instanceof V2OptionMarketPickId || pickId instanceof BetBuilderPickId) {
            const optionMarketSubscription = this.subscriptionFactory.createOptionMarketSubscription(
                context,
                pickId.fixtureId,
                pickId.optionMarketId.toString(),
                lang,
            );
            optionMarketSubscription.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.V2, optionMarketSubscription.key);
            subscriptions.push(optionMarketSubscription);
        }

        if (pickId instanceof V2OptionMarketPickId) {
            const fixtureSubscription = this.subscriptionFactory.createFixtureSlimSubscription(context, pickId.fixtureId, lang);
            fixtureSubscription.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.V2, fixtureSubscription.key);
            subscriptions.push(fixtureSubscription);
        }

        if (pickId instanceof V2ParticipantPickId) {
            const participants = Object.keys(pickId.participantIds);
            for (const participant of participants) {
                const sub = this.subscriptionFactory.createParticipantMarketSubscription(context, pickId.fixtureId, participant, lang);
                sub.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.V2, sub.key);
                subscriptions.push(sub);
            }
        }

        if (pickId instanceof GroupPickId) {
            const sub = this.subscriptionFactory.createBetBuilderSubscription(context);
            sub.callback = this.subscriptionsCallbacksPool.getSubscriptionCallback(OfferSource.BetBuilder, sub.key);
            subscriptions.push(sub);
        }

        if (!subscriptions.length) {
            console.warn(`Did not subscribe to anything for pickId: ${pickId} - check that pick type is valid`);
        }

        this.subscribeToPush(payload, subscriptions);
    }

    private unsubscribeFromPick(pickId: PickId): void {
        const storeSub = this.subscriptionStore.get(pickId.toString());
        if (!storeSub) {
            return;
        }
        storeSub.subscriptionInfo.unsubscribe();
    }

    private cleanStores(): void {
        this.subscriptionStore.clear();
        this.subscriptionsCallbacksPool.clearSubscriptionPool();
    }

    private onPushReconnected = () => {
        const payload: IPickSubscriptionPayload[] = Array.from(this.subscriptionStore.entries()).map(([pickId, sub]) => ({
            pickId: sub.pickId,
            context: sub.subscriptionInfo.subscriptionContext,
        }));
        this.cleanStores();
        this.subscribeToPicks(payload);
    };
}
